import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import {
  HiOutlineArrowPath,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineFunnel,
  HiOutlineMagnifyingGlass,
  HiOutlineUserCircle,
  HiOutlineXCircle
} from "react-icons/hi2";

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#E2E8F0",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626"
};

function formatDisplayDate(dateValue) {
  if (!dateValue) return "-";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function ManagerInbox({ user }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState("");
  const [comments, setComments] = useState({});
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (user?.id) {
      loadRequests();
    }
  }, [user]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get(`/requests/manager/${user.id}`);
      const rows = res.data || [];
      setRequests(rows);

      const initialComments = {};
      rows.forEach((row) => {
        initialComments[row.id] = row.manager_comment || row.comment || "";
      });
      setComments(initialComments);
    } catch (err) {
      console.error("Manager inbox load failed:", err);
      setError("Failed to load manager approvals");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const updateRequest = async (requestId, status) => {
    try {
      setActionLoadingId(requestId);

      await api.put(`/requests/${requestId}`, {
        status,
        manager_comment: comments[requestId] || ""
      });

      await loadRequests();
      window.dispatchEvent(new Event("productionUpdated"));
    } catch (err) {
      console.error("Request update failed:", err);
      alert(err.response?.data?.error || "Failed to update request");
    } finally {
      setActionLoadingId(null);
    }
  };

  const getStatusUI = (row) => {
    const status = String(row.status || "").toLowerCase();

    if (status === "approved") {
      return {
        text: "Approved",
        color: theme.success,
        bg: "#DCFCE7",
        border: "#BBF7D0",
        icon: <HiOutlineCheckCircle />
      };
    }

    if (status === "declined") {
      return {
        text: "Declined",
        color: theme.danger,
        bg: "#FEE2E2",
        border: "#FECACA",
        icon: <HiOutlineXCircle />
      };
    }

    return {
      text: "Pending",
      color: theme.warning,
      bg: "#FEF3C7",
      border: "#FDE68A",
      icon: <HiOutlineClock />
    };
  };

  const filteredRequests = useMemo(() => {
    let rows = requests;

    if (filter !== "all") {
      rows = rows.filter((row) => String(row.status || "").toLowerCase() === filter);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((row) => {
        const employee = String(row.user_name || row.user || "").toLowerCase();
        const reason = String(row.reason || "").toLowerCase();
        const comment = String(row.manager_comment || row.comment || "").toLowerCase();
        const status = String(row.status || "").toLowerCase();
        const date = formatDisplayDate(row.date).toLowerCase();

        return (
          employee.includes(q) ||
          reason.includes(q) ||
          comment.includes(q) ||
          status.includes(q) ||
          date.includes(q)
        );
      });
    }

    return rows;
  }, [requests, filter, search]);

  const pendingCount = requests.filter(
    (r) => String(r.status || "").toLowerCase() === "pending"
  ).length;

  const approvedCount = requests.filter(
    (r) => String(r.status || "").toLowerCase() === "approved"
  ).length;

  const declinedCount = requests.filter(
    (r) => String(r.status || "").toLowerCase() === "declined"
  ).length;

  return (
    <div style={page}>
      <div style={hero}>
        <div>
          <h2 style={title}>Manager Approvals</h2>
          <p style={subtitle}>
            Review late-entry requests, add comments, and approve or decline directly.
          </p>
        </div>

        <button onClick={loadRequests} style={refreshBtn}>
          <HiOutlineArrowPath style={{ fontSize: 16 }} />
          Refresh
        </button>
      </div>

      <div style={summaryGrid}>
        <div style={summaryCard}>
          <p style={summaryLabel}>Total Requests</p>
          <h3 style={summaryValue}>{requests.length}</h3>
        </div>

        <div style={summaryCard}>
          <p style={summaryLabel}>Pending</p>
          <h3 style={{ ...summaryValue, color: theme.warning }}>{pendingCount}</h3>
        </div>

        <div style={summaryCard}>
          <p style={summaryLabel}>Approved</p>
          <h3 style={{ ...summaryValue, color: theme.success }}>{approvedCount}</h3>
        </div>

        <div style={summaryCard}>
          <p style={summaryLabel}>Declined</p>
          <h3 style={{ ...summaryValue, color: theme.danger }}>{declinedCount}</h3>
        </div>
      </div>

      <div style={toolbar}>
        <div style={searchWrap}>
          <HiOutlineMagnifyingGlass style={searchIcon} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employee, reason, status..."
            style={searchInput}
          />
        </div>

        <div style={filterTabs}>
          <button
            type="button"
            onClick={() => setFilter("all")}
            style={{ ...filterBtn, ...(filter === "all" ? activeFilterBtn : {}) }}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setFilter("pending")}
            style={{ ...filterBtn, ...(filter === "pending" ? activeFilterBtn : {}) }}
          >
            Pending
          </button>
          <button
            type="button"
            onClick={() => setFilter("approved")}
            style={{ ...filterBtn, ...(filter === "approved" ? activeFilterBtn : {}) }}
          >
            Approved
          </button>
          <button
            type="button"
            onClick={() => setFilter("declined")}
            style={{ ...filterBtn, ...(filter === "declined" ? activeFilterBtn : {}) }}
          >
            Declined
          </button>
        </div>
      </div>

      {loading && (
        <div style={stateCard}>
          <p style={stateText}>Loading approvals...</p>
        </div>
      )}

      {!loading && error && <div style={errorCard}>{error}</div>}

      {!loading && !error && filteredRequests.length === 0 && (
        <div style={emptyCard}>
          <h3 style={emptyTitle}>No requests found</h3>
          <p style={emptyText}>Requests matching the selected filter will appear here.</p>
        </div>
      )}

      {!loading && !error && filteredRequests.length > 0 && (
        <div style={listCard}>
          <div style={listHeader}>
            <div style={{ ...headerCell, flex: 1.2 }}>Employee</div>
            <div style={{ ...headerCell, flex: 0.85 }}>Request Date</div>
            <div style={{ ...headerCell, flex: 1.6 }}>Reason</div>
            <div style={{ ...headerCell, flex: 1.9 }}>Manager Comment</div>
            <div style={{ ...headerCell, flex: 0.9 }}>Status</div>
            <div style={{ ...headerCell, flex: 1.1, textAlign: "right" }}>Action</div>
          </div>

          {filteredRequests.map((row, index) => {
            const statusUI = getStatusUI(row);
            const isPending = String(row.status || "").toLowerCase() === "pending";
            const isBusy = actionLoadingId === row.id;

            return (
              <div
                key={row.id}
                style={{
                  ...listRow,
                  borderBottom:
                    index === filteredRequests.length - 1 ? "none" : "1px solid #EEF2F7"
                }}
              >
                <div style={{ ...cell, flex: 1.2 }}>
                  <div style={employeeWrap}>
                    <div style={avatarCircle}>
                      <HiOutlineUserCircle style={{ fontSize: 22 }} />
                    </div>
                    <div>
                      <div style={employeeName}>{row.user_name || row.user || "Employee"}</div>
                      <div style={employeeSub}>Late-entry request</div>
                    </div>
                  </div>
                </div>

                <div style={{ ...cell, flex: 0.85 }}>
                  <div style={dateText}>{formatDisplayDate(row.date)}</div>
                </div>

                <div style={{ ...cell, flex: 1.6 }}>
                  <div style={reasonText}>{row.reason || "-"}</div>
                </div>

                <div style={{ ...cell, flex: 1.9 }}>
                  <textarea
                    value={comments[row.id] || ""}
                    onChange={(e) =>
                      setComments((prev) => ({
                        ...prev,
                        [row.id]: e.target.value
                      }))
                    }
                    placeholder="Add comment..."
                    style={{
                      ...commentInput,
                      background: isPending ? "#fff" : "#F8FAFC",
                      color: isPending ? theme.text : theme.textMuted
                    }}
                    disabled={!isPending}
                  />
                </div>

                <div style={{ ...cell, flex: 0.9 }}>
                  <div
                    style={{
                      ...statusPill,
                      background: statusUI.bg,
                      color: statusUI.color,
                      borderColor: statusUI.border
                    }}
                  >
                    <span style={{ display: "flex", fontSize: 15 }}>{statusUI.icon}</span>
                    {statusUI.text}
                  </div>
                </div>

                <div style={{ ...cell, flex: 1.1, alignItems: "flex-end" }}>
                  {isPending ? (
                    <div style={actionStack}>
                      <button
                        type="button"
                        onClick={() => updateRequest(row.id, "approved")}
                        disabled={isBusy}
                        style={{
                          ...approveBtn,
                          opacity: isBusy ? 0.7 : 1,
                          cursor: isBusy ? "not-allowed" : "pointer"
                        }}
                      >
                        {isBusy ? "Processing..." : "Approve"}
                      </button>

                      <button
                        type="button"
                        onClick={() => updateRequest(row.id, "declined")}
                        disabled={isBusy}
                        style={{
                          ...declineBtn,
                          opacity: isBusy ? 0.7 : 1,
                          cursor: isBusy ? "not-allowed" : "pointer"
                        }}
                      >
                        {isBusy ? "Processing..." : "Reject"}
                      </button>
                    </div>
                  ) : (
                    <button type="button" style={doneBtn} disabled>
                      Processed
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================= STYLES ================= */

const page = {
  fontFamily: "sans-serif",
  color: theme.text
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  marginBottom: "20px",
  flexWrap: "wrap"
};

const title = {
  margin: 0,
  fontSize: "24px",
  fontWeight: 700,
  color: theme.text
};

const subtitle = {
  margin: "8px 0 0 0",
  color: theme.textMuted,
  fontSize: "14px",
  lineHeight: 1.6
};

const refreshBtn = {
  border: "none",
  borderRadius: "14px",
  padding: "10px 16px",
  background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  boxShadow: "0 10px 20px rgba(15,118,110,0.18)"
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "16px",
  marginBottom: "18px"
};

const summaryCard = {
  background: theme.surface,
  borderRadius: "18px",
  padding: "18px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)"
};

const summaryLabel = {
  margin: 0,
  fontSize: "13px",
  color: theme.textMuted,
  fontWeight: 600
};

const summaryValue = {
  margin: "10px 0 0 0",
  fontSize: "28px",
  fontWeight: 800,
  color: theme.text
};

const toolbar = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  marginBottom: "18px",
  flexWrap: "wrap"
};

const searchWrap = {
  position: "relative",
  minWidth: "280px",
  flex: 1,
  maxWidth: "430px"
};

const searchIcon = {
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: theme.textMuted,
  fontSize: "16px"
};

const searchInput = {
  width: "100%",
  height: "42px",
  borderRadius: "14px",
  border: "1px solid #D1D5DB",
  padding: "0 12px 0 38px",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
  background: "#fff"
};

const filterTabs = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  alignItems: "center"
};

const filterBtn = {
  border: "1px solid #D1D5DB",
  background: "#fff",
  color: theme.text,
  padding: "10px 16px",
  borderRadius: "12px",
  fontWeight: 700,
  fontSize: "13px",
  cursor: "pointer"
};

const activeFilterBtn = {
  background: theme.surfaceSoft,
  color: theme.primaryDark,
  border: `1px solid ${theme.border}`
};

const stateCard = {
  background: theme.surface,
  borderRadius: "20px",
  padding: "40px 24px",
  border: "1px solid #F1F5F9",
  textAlign: "center"
};

const stateText = {
  margin: 0,
  color: theme.textMuted,
  fontSize: "14px"
};

const errorCard = {
  background: "#FEF2F2",
  border: "1px solid #FECACA",
  color: theme.danger,
  borderRadius: "16px",
  padding: "14px 16px",
  fontWeight: 600
};

const emptyCard = {
  background: theme.surface,
  borderRadius: "20px",
  padding: "48px 24px",
  border: "1px solid #F1F5F9",
  textAlign: "center"
};

const emptyTitle = {
  margin: "0 0 8px 0",
  fontSize: "20px",
  color: theme.text
};

const emptyText = {
  margin: 0,
  color: theme.textMuted,
  fontSize: "14px",
  lineHeight: 1.6
};

const listCard = {
  background: theme.surface,
  borderRadius: "20px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
  overflow: "hidden"
};

const listHeader = {
  display: "flex",
  gap: "16px",
  padding: "16px 18px",
  background: "#F8FAFC",
  borderBottom: "1px solid #E2E8F0"
};

const headerCell = {
  fontSize: "12px",
  fontWeight: 700,
  color: theme.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.04em"
};

const listRow = {
  display: "flex",
  gap: "16px",
  alignItems: "stretch",
  padding: "16px 18px",
  background: "#fff"
};

const cell = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  minWidth: 0
};

const employeeWrap = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  minWidth: 0
};

const avatarCircle = {
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  background: theme.surfaceSoft,
  color: theme.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0
};

const employeeName = {
  fontSize: "15px",
  fontWeight: 700,
  color: theme.text
};

const employeeSub = {
  fontSize: "12px",
  color: theme.textMuted,
  marginTop: "2px"
};

const dateText = {
  fontSize: "13px",
  fontWeight: 600,
  color: theme.text
};

const reasonText = {
  fontSize: "13px",
  color: theme.text,
  lineHeight: 1.6,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word"
};

const commentInput = {
  width: "100%",
  minHeight: "72px",
  padding: "10px 12px",
  borderRadius: "12px",
  border: "1px solid #D1D5DB",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
  resize: "vertical",
  fontFamily: "inherit",
  lineHeight: 1.5
};

const statusPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  padding: "8px 12px",
  borderRadius: "999px",
  fontSize: "13px",
  fontWeight: 700,
  border: "1px solid",
  width: "fit-content"
};

const actionStack = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "8px",
  flexWrap: "wrap",
  marginTop: "4px"
};

const approveBtn = {
  border: "1px solid #A7F3D0",
  borderRadius: "10px",
  padding: "9px 14px",
  background: "#ECFDF5",
  color: theme.success,
  fontSize: "13px",
  fontWeight: 700,
  cursor: "pointer"
};

const declineBtn = {
  border: "1px solid #FECACA",
  borderRadius: "10px",
  padding: "9px 14px",
  background: "#FEF2F2",
  color: theme.danger,
  fontSize: "13px",
  fontWeight: 700,
  cursor: "pointer"
};

const doneBtn = {
  border: "1px solid #E2E8F0",
  borderRadius: "10px",
  padding: "9px 14px",
  background: "#F8FAFC",
  color: theme.textMuted,
  fontSize: "13px",
  fontWeight: 700
};

export default ManagerInbox;